import React from "react";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";

// mui components
import { Box, Stack, TableCell, TableRow } from "@mui/material";

// custom components
import Body3 from "../../typography/body-03";

// mui icons
import {
	FileUploadOutlined,
	ExpandLessOutlined,
	ExpandMoreOutlined,
} from "@mui/icons-material";

import { selectEnhancedConditionStatus } from "../../../features/enhanced-condition-status/enhancedConditionStatusSlice";

// custom styles
import styles from "../index.module.scss";

const ConditionTrackingRow = ({
	row,
	handleUploadClick,
	handleDownloadClick,
	minimizedRow,
	handleAddMinimizeRow,
	handleDeleteMinimizeRow,
}) => {
	const enhancedConditionsStatus = useSelector(selectEnhancedConditionStatus);
	const isEnhancedConditionsEnabled =
		String(enhancedConditionsStatus ?? "")
			.trim()
			.toUpperCase() === "Y";
	const descriptionText = String(
		isEnhancedConditionsEnabled ? row.externalDescription : row.description
	)
		.trim();

	return (
		<TableRow hover tabIndex={-1} key={row.label}>
			<TableCell>
				<Body3 text={row.status} extraClass={styles.greenText} />
			</TableCell>
			<TableCell>
				<Body3
					text={row.sub_status}
					extraClass={styles.greenText}
				/>
			</TableCell>
			<TableCell>
				<Body3 text={row.category} extraClass={styles.greenText} />
			</TableCell>
			<TableCell>
				<Stack direction="row" className={styles.overflowTextContainer}>
					<Body3
						text={descriptionText}
						extraClass={
							minimizedRow?.includes(row?.id)
								? styles.truncatedText
								: styles.truncatedText + " " + styles.expandedText
						}
					/>
					{descriptionText &&
						(minimizedRow?.includes(row?.id) ? (
							<ExpandMoreOutlined
								onClick={() => handleDeleteMinimizeRow(row?.id)}
								className={styles.truncatedIcon}
							/>
						) : (
							<ExpandLessOutlined
								onClick={() => handleAddMinimizeRow(row?.id)}
								className={styles.truncatedIcon}
							/>
						))}
				</Stack>
			</TableCell>
			<TableCell
				onClick={(event) => handleUploadClick(event, row.id)}
				className={styles.textCenter + " " + styles.minimizedWidth}
			>
				<Box className={styles.uploadIconContainer}>
					<FileUploadOutlined className={styles.uploadIcon} />
				</Box>
			</TableCell>
			{isEnhancedConditionsEnabled ? (
				<TableCell
					onClick={(event) => {
						const assignedIds =
							row?.assignedTo?.map((item) => item.entityId) ?? [];

						const timestamp = row?.lastAttachmentDate;

						if (assignedIds.length > 0 && timestamp) {
							handleDownloadClick(event, assignedIds);
						}
					}}
					className={`${styles.minimizedWidth} ${
						row?.assignedTo?.length > 0 && row?.lastAttachmentDate
							? styles.clickable
							: styles.notClickable
					}`}
				>
					<Body3
						text={
							row?.assignedTo?.length > 0 && row?.lastAttachmentDate
								? DateTime.fromISO(row?.lastAttachmentDate, {
										zone: "America/New_York",
									}).toFormat("MM/dd/yyyy hh:mm a")
								: "NA"
						}
						fontType="semibold"
						extraClass={
							row?.assignedTo?.length > 0 && row?.lastAttachmentDate
								? styles.conditionTimestampLink
								: styles.conditionTimestamp
						}
					/>
				</TableCell>
			) : (
				<TableCell
					onClick={(event) => {
						const documentIds =
							row?.documents?.map((doc) => doc.entityId) ?? [];

						const timestamp =
							row?.attachment_upload_timestamp ?? row?.createdDate;

						if (documentIds.length > 0 && timestamp) {
							handleDownloadClick(event, documentIds);
						}
					}}
					className={`${styles.minimizedWidth} ${
						row?.documents?.length > 0 &&
						(row?.attachment_upload_timestamp || row?.createdDate)
							? styles.clickable
							: styles.notClickable
					}`}
				>
					<Body3
						text={
							row?.documents?.length > 0 &&
							(row?.attachment_upload_timestamp || row?.createdDate)
								? DateTime.fromISO(
										row?.attachment_upload_timestamp ?? row?.createdDate,
										{
											zone: "America/New_York",
										}
									).toFormat("MM/dd/yyyy hh:mm a")
								: "NA"
						}
						fontType="semibold"
						extraClass={
							row?.documents?.length > 0 &&
							(row?.attachment_upload_timestamp || row?.createdDate)
								? styles.conditionTimestampLink
								: styles.conditionTimestamp
						}
					/>
				</TableCell>
			)}
		</TableRow>
	);
};

export default ConditionTrackingRow;
